import ApiService from './';
import { EditProfileParams, LoginParams, LoginResponse, SignUpParams, SignUpResponse, UpdatePasswordParams } from '../params/auth';

/**
* @heading1: Authentication API Service
 *
 * @paragraph: This module provides a set of methods to interact with the Authentication API.
 * It handles operations like user login, signup, and logout.
 */

const AuthApi = {
    /**
     * @heading3: Login Function
     *
     * @paragraph: Authenticates the user by verifying their credentials.
     *
     * @param: {LoginParams} data - The user credentials (email and password) to authenticate.
     * @returns: {Promise<LoginResponse>} - A promise that resolves to the login response containing authentication details.
     * @code:
     * AuthApi.login({ email: 'email', password: 'password123' })
     *   .then(response => console.log(response.token))
     *   .catch(error => console.error(error));
     * @separator:
     */
    login: (data: LoginParams): Promise<LoginResponse> => {
        // Perform a POST request to the login endpoint with the user data
        return ApiService.apiHit<LoginResponse>({
            url: 'login',
            method: 'post',
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: SignUp Function
     *
     * Creates a new user account with the provided details.
     *
     * @param: {SignUpParams} data - The user details (first name, last name, organization name, email, and password) for signup.
     * @returns: {Promise<SignUpResponse>} - A promise that resolves to the signup response containing the created user details.
     * @code:
     * AuthApi.signup({
     * "first_name": "John",
     * "last_name": "Doe",
     * "email": "john.doe@example.com",
     * "organization_name": "Acme Inc.",
     * "password": "Password123!",})
     *   .then(response => console.log(response.token))
     *   .catch(error => console.error(error));
     * @separator:
     */
    signup: (data: SignUpParams): Promise<SignUpResponse> => {
        // Perform a POST request to the login endpoint with the user data
        return ApiService.apiHit<SignUpResponse>({
            url: 'signup',
            method: 'post',
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Logout Function
     *
     * Logs the user out by invalidating their session token.
     *
     * @param: {string} token - The authentication token to invalidate.
     * @returns: {Promise<void>} - A promise that resolves when the logout is successful.
     * @code:
     * AuthApi.logout('auth-token')
     *   .then(() => console.log('Logged out successfully'))
     *   .catch(error => console.error('Logout failed:', error));
     * @separator:
     */
    logout: (token: string | undefined) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({ url: 'logout', token, method: 'post' });
    },
    /**
     * @heading3: Logout Function
     *
     * Logs the user out by invalidating their session token.
     *
     * @param: {string} token - The authentication token to invalidate.
     * @returns: {Promise<void>} - A promise that resolves when the logout is successful.
     * @code:
     * AuthApi.logout('auth-token')
     *   .then(() => console.log('Logged out successfully'))
     *   .catch(error => console.error('Logout failed:', error));
     * @separator:
     */
    getPlans: (token: string) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({ url: 'get-plan', token, method: 'post' });
    },
    /**
     * @heading3: Logout Function
     *
     * Logs the user out by invalidating their session token.
     *
     * @param: {string} token - The authentication token to invalidate.
     * @returns: {Promise<void>} - A promise that resolves when the logout is successful.
     * @code:
     * AuthApi.logout('auth-token')
     *   .then(() => console.log('Logged out successfully'))
     *   .catch(error => console.error('Logout failed:', error));
     * @separator:
     */
    checkoutPlan: (token: string, plan_id: number) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({
            url: 'create-checkout-session',
            token,
            method: 'post',
            data: { plan_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    updatePassword: (token: string, data: UpdatePasswordParams) => {
        return ApiService.apiHit({
            url: 'update-password',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    editProfile: (token: string, data: EditProfileParams) => {
        return ApiService.apiHit({
            url: 'update-user',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    deactivateAccount: (token: string) => {
        return ApiService.apiHit({
            url: 'deactivate-account',
            method: 'post',
            token,
            data: {},
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    activateAccount: (token: string) => {
        return ApiService.apiHit({
            url: 'activate-account',
            method: 'post',
            token,
            data: {},
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchUserDetail: (token: string) => {
        return ApiService.apiHit({
            url: 'get-user-details',
            method: 'post',
            token,
            data: {},
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    sendResetPasswordEmail: (token: string, email: string) => {
        return ApiService.apiHit({
            url: 'reset-password-email',
            method: 'post',
            token,
            data: { email },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    setResetPassword: (token: string, code: string, password: string) => {
        return ApiService.apiHit({
            url: 'reset-password',
            method: 'post',
            token,
            data: { code, password },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default AuthApi;
